import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { UrlParams } from '@provision/portal-routes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DynamicIcon, MetaTags, Tooltip } from 'src/components';
import { BlockContainer } from 'src/components/Layout/named-layout-block.view';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useCurrentUserProfile } from 'src/graphql';
import {
  AnyCustomViewEntity,
  CustomViewFormFields,
  CustomViewFormModel,
  CustomViewSorting,
  useCustomViewEntityConfig,
} from 'src/graphql/entities/custom-view';
import {
  mapConditionsForApi,
  useCustomViews,
} from 'src/graphql/entities/custom-view/custom-view';
import { getCustomAttributeDisplayPaths } from 'src/graphql/entities/custom-view/custom-view-config/custom-attributes-bridge';
import { mapSortAttributeToFormInterface } from 'src/graphql/entities/custom-view/custom-view-config/utils';
import { MAP_URL_ENTITY_NAME_TO_ENTITY } from 'src/pages/CustomViews/constants';
import { CustomViewsEntitiesTable } from 'src/pages/CustomViews/custom-views-entities-table';
import { useGlobalStore } from 'src/store';
import { useNavigateRoute, useRouteParams } from 'src/utils/routing';
import { CustomViewForm } from './custom-views-form';

export const CustomViewsFormView = () => {
  const location = useLocation();
  const { navigate } = useNavigateRoute();
  const [formValues, setFormValues] = useState<CustomViewFormModel | null>(null);
  const [sorting, setSorting] = useState<CustomViewSorting | null>();

  const { user } = useCurrentUserProfile();
  const { entityName } = useRouteParams(UrlParams.entityName);

  const selectedEntity = MAP_URL_ENTITY_NAME_TO_ENTITY[entityName] ?? entityName;

  const { customViewId: customViewIdForUpdate } = useRouteParams(UrlParams.customViewId);

  const theme = useTheme();
  const partnerId = useGlobalStore.use.selectedPartnerId() ?? PLATFORM_PARTNER_ID;

  const {
    attributesList,
    attributesMap,
    useViewEntities,
    filterAttributesMap,
    filterAttributesList,
    columnRenderersById,
  } = useCustomViewEntityConfig(selectedEntity);

  const { customViews, createCustomView, updateCustomView } = useCustomViews({
    entity: selectedEntity,
    partnerId,
  });

  const goBack = useCallback(() => navigate(-1), [navigate]);

  const viewToBeUpdated = useMemo(() => {
    const foundView = customViews.find((view) => view.customViewId === customViewIdForUpdate);

    if (!foundView) {
      return null;
    }

    return {
      ...foundView,
      [CustomViewFormFields.Conditions]: foundView.formConditions,
    };
  }, [customViews, customViewIdForUpdate]);

  const handleSubmitCustomViewForm = useCallback(
    (values: CustomViewFormModel) => {
      const data = {
        ...values,
        sort: sorting!,
        conditions: mapConditionsForApi(values.conditions),
        entityName: selectedEntity,
      };

      if (customViewIdForUpdate === 'new') {
        createCustomView({ ...data, partnerId });
      } else if (viewToBeUpdated && customViewIdForUpdate) {
        updateCustomView(viewToBeUpdated, data);
      }
      goBack();
    },
    [
      sorting,
      goBack,
      createCustomView,
      updateCustomView,
      customViewIdForUpdate,
      partnerId,
      selectedEntity,
      viewToBeUpdated,
    ],
  );

  const { initialValues } =
    (location.state as {
      initialValues?: CustomViewFormModel & { sort: CustomViewSorting };
    }) ?? {};

  useEffect(() => {
    let sortBy = initialValues?.sort ?? viewToBeUpdated?.sort;

    if (!sortBy) {
      const attribute = attributesList.find((i) => i.default && i.sortingKey)!;

      sortBy = mapSortAttributeToFormInterface(attribute);
    }

    setSorting(sortBy);
  }, [viewToBeUpdated, initialValues, attributesList, setSorting]);

  const mappedApiFilters = useMemo(() => {
    return (
      (formValues?.conditions &&
        mapConditionsForApi(formValues?.conditions, { user, interpolateValues: true })) ??
      []
    );
  }, [formValues?.conditions, user]);

  const dynamicAttributePaths = useMemo(
    () => getCustomAttributeDisplayPaths(formValues?.columns),
    [formValues?.columns],
  );

  const { records, totalRecords } = useViewEntities({
    filters: mappedApiFilters,
    sort: sorting,
    limit: 5,
    dynamicAttributePaths,
  });

  const handleSort = useCallback(
    (params: { id: string; desc: boolean }) => {
      if (!params) {
        setSorting(null);
        return;
      }

      const attributeData = attributesMap.get(params.id);

      if (!attributeData) {
        return;
      }

      setSorting(mapSortAttributeToFormInterface(attributeData, params));
    },
    [setSorting, attributesMap],
  );

  const viewTitle = customViewIdForUpdate === 'new' ? 'Create View' : 'Update View';

  return (
    <Box m={theme.spacing(2)} height="100%">
      <MetaTags title={viewTitle} />
      <BlockContainer sx={{ boxShadow: theme.shadows['4'], height: 'auto' }}>
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          paddingY={theme.spacing(0.3)}
          sx={{
            borderBottom: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
          }}
          paddingX={theme.spacing(1)}
        >
          <Typography
            variant="caption"
            sx={{
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {viewTitle}
          </Typography>
          <Tooltip title="close">
            <IconButton
              sx={{
                padding: 0.5,
                borderRadius: theme.shape.borderRadius / 8,
              }}
              onClick={() => navigate(-1)}
            >
              <DynamicIcon name="close" />
            </IconButton>
          </Tooltip>
        </Stack>
        <CustomViewForm
          key={selectedEntity}
          entityName={selectedEntity}
          filterAttributesMap={filterAttributesMap}
          filterAttributesList={filterAttributesList}
          canBePrivate={
            !viewToBeUpdated || viewToBeUpdated?.createdByUser?.userId === user?.userId
          }
          onCancel={goBack}
          attributes={attributesList}
          initialValues={initialValues || viewToBeUpdated}
          setValues={setFormValues}
          onSubmit={handleSubmitCustomViewForm}
        />
      </BlockContainer>

      <Box mt="1.3rem">
        <CustomViewsEntitiesTable<AnyCustomViewEntity>
          totalRecords={totalRecords > 5 ? 5 : totalRecords}
          entityName={selectedEntity}
          rows={records}
          selectedColumns={formValues?.columns}
          sort={sorting}
          onSortChange={handleSort}
          customAttributeRenderers={columnRenderersById}
        />
      </Box>
    </Box>
  );
};

export default CustomViewsFormView;
